import React, { useState } from 'react';
import { Card, Table, Button, Input, Space, Tag, Typography, Row, Col, Select, Switch } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined, KeyOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

interface Merchant {
  key: string;
  id: string;
  merchantName: string;
  merchantCode: string;
  contactPerson: string;
  phone: string;
  email: string;
  status: 'active' | 'inactive' | 'pending';
  balance: number;
  createTime: string;
  lastLoginTime: string;
}

const MerchantManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);

  // 模拟数据
  const mockData: Merchant[] = [
    {
      key: '1',
      id: 'MER001',
      merchantName: '北京科技有限公司',
      merchantCode: 'BJ001',
      contactPerson: '张三',
      phone: '13800138001',
      email: 'zhangsan@example.com',
      status: 'active',
      balance: 50000.00,
      createTime: '2024-01-01',
      lastLoginTime: '2024-01-15 14:30:00',
    },
    {
      key: '2',
      id: 'MER002',
      merchantName: '上海贸易公司',
      merchantCode: 'SH001',
      contactPerson: '李四',
      phone: '13800138002',
      email: 'lisi@example.com',
      status: 'active',
      balance: 25000.50,
      createTime: '2024-01-02',
      lastLoginTime: '2024-01-14 09:15:00',
    },
    {
      key: '3',
      id: 'MER003',
      merchantName: '深圳测试公司',
      merchantCode: 'SZ001',
      contactPerson: '王五',
      phone: '13800138003',
      email: 'wangwu@example.com',
      status: 'pending',
      balance: 0.00,
      createTime: '2024-01-03',
      lastLoginTime: '-',
    },
    {
      key: '4',
      id: 'MER004',
      merchantName: '广州物流公司',
      merchantCode: 'GZ001',
      contactPerson: '赵六',
      phone: '13800138004',
      email: 'zhaoliu@example.com',
      status: 'inactive',
      balance: 1000.00,
      createTime: '2024-01-04',
      lastLoginTime: '2024-01-10 16:45:00',
    },
  ];

  const columns = [
    {
      title: '商户名称',
      dataIndex: 'merchantName',
      key: 'merchantName',
    },
    {
      title: '商户代码',
      dataIndex: 'merchantCode',
      key: 'merchantCode',
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap = {
          active: { text: '正常', color: 'green' },
          inactive: { text: '停用', color: 'red' },
          pending: { text: '待审核', color: 'orange' },
        };
        const statusInfo = statusMap[status as keyof typeof statusMap];
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '账户余额',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance: number) => `¥${balance.toLocaleString()}`,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginTime',
      key: 'lastLoginTime',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: Merchant) => (
        <Space size="middle">
          <Button type="link" size="small">查看</Button>
          <Button type="link" size="small">编辑</Button>
          <Button type="link" size="small" icon={<KeyOutlined />}>重置密码</Button>
          <Button type="link" size="small">充值</Button>
          {record.status === 'active' ? (
            <Button type="link" size="small" danger>停用</Button>
          ) : (
            <Button type="link" size="small">启用</Button>
          )}
        </Space>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    console.log('搜索:', value);
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>商户号管理</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<PlusOutlined />} type="primary">
                新增商户
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Search
                placeholder="搜索商户名称、代码、联系人"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: 300 }}
                onSearch={handleSearch}
              />
              <Select placeholder="商户状态" style={{ width: 120 }} allowClear>
                <Option value="active">正常</Option>
                <Option value="inactive">停用</Option>
                <Option value="pending">待审核</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button>批量操作</Button>
              <Button>导出数据</Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={mockData}
          loading={loading}
          pagination={{
            total: mockData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>
    </div>
  );
};

export default MerchantManagement; 